import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
interface WorkflowStep {
  id: string;
  name: string;
  description: string;
  order: number;
  isCompleted: boolean;
  completedAt?: string;
  assignedTo?: {
    userId: string;
    userName: string;
  };
}
interface WorkflowInstance {
  id: string;
  applicationId: string;
  workflowId: string;
  workflowName: string;
  currentStepIndex: number;
  steps: WorkflowStep[];
  startedAt: string;
  updatedAt: string;
  completedAt?: string;
}
interface WorkflowContextType {
  getWorkflowInstanceByApplicationId: (applicationId: string) => WorkflowInstance | undefined;
  updateWorkflowStep: (workflowId: string, stepId: string, updates: Partial<WorkflowStep>) => void;
  advanceWorkflow: (workflowId: string) => void;
}
const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined);
// Sample workflow instances
const sampleWorkflowInstances: WorkflowInstance[] = [{
  id: uuidv4(),
  applicationId: 'app-001',
  workflowId: 'wf-tax-clearance',
  workflowName: 'Tax Clearance Certificate Process',
  currentStepIndex: 3,
  steps: [{
    id: 'step-1',
    name: 'Application Submission',
    description: 'Application is submitted by the applicant',
    order: 0,
    isCompleted: true,
    completedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
  }, {
    id: 'step-2',
    name: 'Payment Verification',
    description: 'Payment is verified by the finance department',
    order: 1,
    isCompleted: true,
    completedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    assignedTo: {
      userId: 'staff-001',
      userName: 'Finance Officer'
    }
  }, {
    id: 'step-3',
    name: 'Tax Record Verification',
    description: 'Verification of tax payment records',
    order: 2,
    isCompleted: true,
    completedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    assignedTo: {
      userId: 'staff-002',
      userName: 'Tax Officer'
    }
  }, {
    id: 'step-4',
    name: 'Certificate Generation',
    description: 'Generation of tax clearance certificate',
    order: 3,
    isCompleted: false,
    assignedTo: {
      userId: 'staff-003',
      userName: 'Certificate Officer'
    }
  }, {
    id: 'step-5',
    name: 'Certificate Approval',
    description: 'Final approval by the tax authority',
    order: 4,
    isCompleted: false
  }],
  startedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
}];
export const WorkflowProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [workflowInstances, setWorkflowInstances] = useState<WorkflowInstance[]>([]);
  // Load workflow instances from localStorage or use sample data
  useEffect(() => {
    const savedWorkflowInstances = localStorage.getItem('workflowInstances');
    if (savedWorkflowInstances) {
      setWorkflowInstances(JSON.parse(savedWorkflowInstances));
    } else {
      setWorkflowInstances(sampleWorkflowInstances);
      localStorage.setItem('workflowInstances', JSON.stringify(sampleWorkflowInstances));
    }
  }, []);
  // Save workflow instances to localStorage when they change
  useEffect(() => {
    localStorage.setItem('workflowInstances', JSON.stringify(workflowInstances));
  }, [workflowInstances]);
  // Get workflow instance by application ID
  const getWorkflowInstanceByApplicationId = useCallback((applicationId: string) => {
    return workflowInstances.find(workflow => workflow.applicationId === applicationId);
  }, [workflowInstances]);
  // Update workflow step
  const updateWorkflowStep = useCallback((workflowId: string, stepId: string, updates: Partial<WorkflowStep>) => {
    setWorkflowInstances(prev => prev.map(workflow => {
      if (workflow.id === workflowId) {
        const updatedSteps = workflow.steps.map(step => step.id === stepId ? {
          ...step,
          ...updates
        } : step);
        return {
          ...workflow,
          steps: updatedSteps,
          updatedAt: new Date().toISOString()
        };
      }
      return workflow;
    }));
  }, []);
  // Advance workflow to next step
  const advanceWorkflow = useCallback((workflowId: string) => {
    setWorkflowInstances(prev => prev.map(workflow => {
      if (workflow.id === workflowId) {
        const currentStepIndex = workflow.currentStepIndex;
        const nextStepIndex = currentStepIndex + 1;
        // If there's no next step, we're done
        if (nextStepIndex >= workflow.steps.length) {
          return workflow;
        }
        // Mark current step as completed
        const updatedSteps = workflow.steps.map((step, index) => {
          if (index === currentStepIndex) {
            return {
              ...step,
              isCompleted: true,
              completedAt: new Date().toISOString()
            };
          }
          return step;
        });
        return {
          ...workflow,
          currentStepIndex: nextStepIndex,
          steps: updatedSteps,
          updatedAt: new Date().toISOString(),
          completedAt: nextStepIndex === workflow.steps.length - 1 ? new Date().toISOString() : undefined
        };
      }
      return workflow;
    }));
  }, []);
  return <WorkflowContext.Provider value={{
    getWorkflowInstanceByApplicationId,
    updateWorkflowStep,
    advanceWorkflow
  }} data-id="element-819">
      {children}
    </WorkflowContext.Provider>;
};
export const useWorkflow = () => {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};